import React, { useEffect, useState, lazy, Suspense } from "react";
import { db } from "../../firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import "./PDFSummaryReport.css";
import { useLocation } from "react-router-dom";
import { PieChart } from "react-minimal-pie-chart";
import { FaCalendarAlt, FaFilePdf, FaFileExcel } from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const getToday = () => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
};

const Navbar = lazy(() => import("./Navbar"));
const NavbarApp = lazy(() => import("./NavbarApp"));

const SharePage = () => {
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [students, setStudents] = useState([]);
  const [adminEmails, setAdminEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [availableCourses, setAvailableCourses] = useState([]);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [modules, setModules] = useState([]);
  const [companyName, setCompanyName] = useState("Loading...");
  const [programName, setProgramName] = useState("");
  const [trainerName, setTrainerName] = useState("");
  const [attendanceData, setAttendanceData] = useState({
    Present: 0,
    Absent: 0,
    Late: 0,
  });

  const location = useLocation();
  const companyCode = localStorage.getItem("companyCode");
  const NavbarComponent = location.pathname.includes("/app") ? NavbarApp : Navbar;
  const todayFormatted = getToday();

  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const companyRef = doc(db, "CorporateClients", companyCode);
        const companySnap = await getDoc(companyRef);
        if (companySnap.exists()) {
          setCompanyName(companySnap.data().companyName || "Unknown");
        } else {
          setCompanyName("Unknown");
        }
      } catch (error) {
        console.error("Error fetching company name:", error);
        setCompanyName("Error");
      }
    };

    if (companyCode) {
      fetchCompanyName();
    }
  }, [companyCode]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const companyDocRef = doc(db, "CorporateClients", companyCode);
        const companySnapshot = await getDoc(companyDocRef);

        if (companySnapshot.exists()) {
          const data = companySnapshot.data();
          const admins = data.adminEmails || [];
          setAdminEmails(admins);

          const companyName = data.companyName || companyCode;
          setCompanyName(companyName);

          const programName = data.programName || "Not Defined";
          setProgramName(programName);

          const trainer = data.trainerName || "";
          setTrainerName(trainer);

          const studentsRef = collection(db, "CorporateClients", companyCode, "studentInfo");
          const snapshot = await getDocs(studentsRef);

          const studentList = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .filter((student) => !/trainer|lsm/i.test(student.fullName || ""));

          setStudents(studentList);
        } else {
          alert("Invalid company code.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyCode]);

  const getAvailableCourses = async (companyCode) => {
    try {
      const coursesRef = collection(db, `CorporateClients/${companyCode}/programInfo`);
      const snapshot = await getDocs(coursesRef);
      const courses = snapshot.docs.map(doc => doc.id);
      return courses;
    } catch (error) {
      console.error("Error fetching courses:", error);
      return [];
    }
  };

  const CourseSelectionModal = ({ courses, onSelectCourse, onClose }) => {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '100%'
        }}>
          <h3>Select a Course to Share</h3>
          <div style={{ margin: '20px 0' }}>
            {courses.map(course => (
              <button
                key={course}
                onClick={() => onSelectCourse(course)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px',
                  margin: '5px 0',
                  cursor: 'pointer'
                }}
              >
                {course}
              </button>
            ))}
          </div>
          <button onClick={onClose} style={{ padding: '8px 16px' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const handleMarksShare = async (e) => {
    e.preventDefault();

    if (!companyCode) {
      alert("Company code is missing.");
      return;
    }

    try {
      const courses = await getAvailableCourses(companyCode);

      if (courses.length === 0) {
        alert("No courses found for this company.");
        return;
      }

      setAvailableCourses(courses);
      setShowCourseModal(true);
    } catch (error) {
      console.error("Error sharing marks:", error);
      alert("An error occurred while sharing marks.");
    }
  };
  let hasNonZeroAssignment = false;

  students.forEach((student) => {
    const courses = student.marks || {};
    if (courses[selectedCourse]) {
      Object.entries(courses[selectedCourse]).forEach(([module, scores]) => {
        const assignmentScore = Number(scores.assignment ?? 0);
        if (assignmentScore > 0) {
          hasNonZeroAssignment = true;
        }
      });
    }
  });

  const handleCourseSelection = async (selectedCourse) => {
    setSelectedCourse(selectedCourse);
    setShowCourseModal(false);

    try {
      let hasNonZeroAssignment = false;
      const studentData = {};
      let allRows = [];

      // First pass: detect if any assignment is non-zero
      students.forEach((student) => {
        const courses = student.marks || {};
        if (courses[selectedCourse]) {
          Object.entries(courses[selectedCourse]).forEach(([module, scores]) => {
            const assignmentScore = Number(scores.assignment ?? 0);
            if (assignmentScore > 0 && assignmentScore !== -1) {
              hasNonZeroAssignment = true;
            }
          });
        }
      });

      // Organize data by student
      students.forEach((student) => {
        const fullName = student.fullName || "N/A";
        const studentId = student.id || "";
        const courses = student.marks || {};

        if (courses[selectedCourse]) {
          const studentModules = [];

          Object.entries(courses[selectedCourse]).forEach(([module, scores]) => {
            // Format module name (M01 -> Module 01) and extract numeric part
            const moduleNumber = module.match(/\d+/)?.[0]?.padStart(2, '0') || '00';
            const formattedModule = module || "N/A";

            // Round and handle -1 to "N/A"

            let quizScore = Number(scores.quiz ?? 0);
            let assignmentScore = Number(scores.assignment ?? 0);

            quizScore = quizScore === -1 ? "N/A" : Math.round((quizScore / 40) * 100);
            assignmentScore = assignmentScore === -1 ? "N/A" : Math.round((assignmentScore / 60) * 100);
            const comment = scores.comment || "";

            studentModules.push({
              module: formattedModule,
              moduleNumber: parseInt(moduleNumber),
              quiz: quizScore,
              assignment: assignmentScore,
              comment
            });
          });

          // Sort modules by their numeric value
          studentModules.sort((a, b) => a.moduleNumber - b.moduleNumber);

          studentData[studentId] = {
            name: fullName,
            modules: studentModules
          };

          // Add to allRows array with student info
          studentModules.forEach((module, index) => {
            allRows.push({
              studentId,
              name: fullName,
              course: selectedCourse,
              module: module.module,
              quiz: module.quiz,
              assignment: module.assignment,
              comment: module.comment,
              isFirstRow: index === 0,
              moduleCount: studentModules.length
            });
          });
        }
      });

      // Sort all rows by student ID
      allRows.sort((a, b) => a.studentId.localeCompare(b.studentId));

      let tableBody = "";
      let rowCounter = 1;

      allRows.forEach((row) => {
        tableBody += `
        <tr>
          
          ${row.isFirstRow ?
            `<td rowspan="${row.moduleCount}">${row.name}</td>` : ''}
          ${row.isFirstRow ?
            `<td rowspan="${row.moduleCount}">${row.course}</td>` : ''}
          <td>${row.module}</td>
		  ${hasNonZeroAssignment ? `<td>${row.assignment}</td>` : ""}
          <td>${row.quiz}</td>
          <td>${row.comment}</td>
        </tr>
      `;
        rowCounter++;
      });





      if (allRows.length === 0) {
        tableBody = `
        <tr>
          <td colspan="6" style="text-align:center;">No student records found for ${selectedCourse}.</td>
        </tr>
      `;
      }

      const htmlTable = `
      <table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif;">
        <thead style="background-color: #f2f2f2;">
          <tr>
            
            <th>Name</th>
            <th>Course</th>
            <th>Module</th>
			${hasNonZeroAssignment ? `<th>Assignment %</th>` : ""}
            <th>Score</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          ${tableBody}
        </tbody>
      </table>
    `;

      const messageHtml = `
      <div style="font-family: Arial, sans-serif; font-size: 14px;">
        <p>Dear Admin,</p>
        <p>Please find below the <strong>Assessment report</strong> for <strong>${selectedCourse}</strong> at <strong>${companyName}</strong>.</p>
        ${htmlTable}
        
        <div style="margin-top: 20px; background-color: #f8f9fa; padding: 15px; border-left: 4px solid #007bff;">
          <p style="margin: 0; font-weight: bold;">Scoring Note:</p>
          <p style="margin: 5px 0 0 0;">Scores are calculated as: (Obtained Marks / Total Marks) × 100</p>
          <p style="margin: 5px 0 0 0;">Example: If a student scored 28 out of 40 → (28/40) × 100 = 70</p>
        </div>
        
        <p style="margin-top: 20px;">Feel free to reach out in case of any queries or clarifications.</p>
        <p>Best regards,<br/><strong>Naveen Pn</strong><br/>Corporate Trainer</p>
      </div>
    `;

      for (const email of adminEmails) {
        const templateParams = {
          to_email: email,
          message: messageHtml,
          title: `Assessment Summary – ${selectedCourse} – ${companyName}`,
        };

        await emailjs.send("service_m0uhadr", "template_zniheem", templateParams, "uKaFTJVfykxCI6cPs");
      }

      alert(`Marks for ${selectedCourse} shared successfully!`);
    } catch (error) {
      console.error("Error sharing marks:", error);
      alert("An error occurred while sharing marks.");
    }
  };

  const handleAttendanceShare = (e) => {
    e.preventDefault();

    const sortedStudents = [...students].sort((a, b) =>
      (a.fullName || "").localeCompare(b.fullName || "")
    );

    const tableHeader = `
      <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif;">
        <thead>
          <tr style="background-color: #f2f2f2;">
            <th>#</th>
            <th>Name</th>
            <th>${selectedDate} (Today)</th>
            <th>Days Present</th>
            <th>Days Absent</th>
            <th>Days Late</th>
          </tr>
        </thead>
        <tbody>
    `;

    const tableRows = sortedStudents
      .map((student, index) => {
        const attendance = student.attendance || {};
        const comments = student.comments || {};

        let presentCount = 0;
        let absentCount = 0;
        let lateCount = 0;

        Object.values(attendance).forEach((status) => {
          if (status === "Present") presentCount++;
          else if (status === "Absent") absentCount++;
          else if (status === "Late") lateCount++;
        });

        const selectedDateStatus = attendance[selectedDate] || "N/A";
        const selectedDateComment = comments[selectedDate]?.trim();

        let coloredStatus;
        switch (selectedDateStatus) {
          case "Present":
            coloredStatus = `<span style="color: green; font-weight: bold;">${selectedDateStatus}</span>`;
            break;
          case "Absent":
            coloredStatus = `<span style="color: red; font-weight: bold;">${selectedDateStatus}</span>`;
            break;
          case "Late":
            coloredStatus = `<span style="color: orange; font-weight: bold;">${selectedDateStatus}</span>`;
            break;
          default:
            coloredStatus = `<span>${selectedDateStatus}</span>`;
        }

        const statusWithComment = selectedDateComment
          ? `${coloredStatus}<br/><i style="color:gray">(${selectedDateComment})</i>`
          : coloredStatus;

        return `
          <tr>
            <td>${index + 1}</td>
            <td>${student.fullName || "N/A"}</td>
            <td>${statusWithComment}</td>
            <td>${presentCount}</td>
            <td>${absentCount}</td>
            <td>${lateCount}</td>
          </tr>
        `;
      })
      .join("");

    const tableFooter = `</tbody></table>`;

    const messageHtml = `
      <div style="font-family: Arial, sans-serif; font-size: 14px;">
        <p>Dear Team,</p>
        <p>
        Please find below the <strong>student attendance summary</strong> for <strong>${companyName}</strong> dated <strong>${selectedDate}</strong>:</p>
        ${tableHeader + tableRows + tableFooter}
        <p>
        If you notice any discrepancies, please don't hesitate to get in touch.
      </p>
      <p>Best regards,<br/>
      <strong>Naveen Pn</strong><br/>
      Corporate Trainer</p>
      </div>
    `;

    adminEmails.forEach((email) => {
      const templateParams = {
        to_email: email,
        message: messageHtml,
        title: `Attendance Summary – ${companyName}`,
      };

      emailjs
        .send(
          "service_m0uhadr",
          "template_zniheem",
          templateParams,
          "uKaFTJVfykxCI6cPs"
        )
        .then(() => {
          console.log(`Email sent to ${email}`);
        })
        .catch((error) => {
          console.error(`Failed to send email to ${email}:`, error);
        });
    });

    alert(`Attendance for ${selectedDate} sent to all admin emails.`);
  };



  const handleSummaryShare = async (e, companyCode, adminEmails, companyName) => {
    e.preventDefault();

    if (!companyCode) {
      alert("Company code is missing.");
      return;
    }

    try {
      const sortedStudents = [...students].sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));

      // Attendance Table
      const tableHeader = `
      <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif;">
        <thead style="background-color: #f2f2f2;">
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Days Present</th>
            <th>Days Absent</th>
            <th>Days Late</th>
          </tr>
        </thead>
        <tbody>
    `;

      const tableRows = sortedStudents
        .map((student, index) => {
          const attendance = student.attendance || {};
          let presentCount = 0;
          let absentCount = 0;
          let lateCount = 0;

          Object.values(attendance).forEach((status) => {
            if (status === "Present") presentCount++;
            else if (status === "Absent") absentCount++;
            else if (status === "Late") lateCount++;
          });

          return `
          <tr>
            <td>${index + 1}</td>
            <td>${student.fullName || "N/A"}</td>
            <td>${presentCount}</td>
            <td>${absentCount}</td>
            <td>${lateCount}</td>
          </tr>
        `;
        })
        .join("");

      const tableFooter = `</tbody></table>`;

      // Marks Summary Table
      const marksTableHeader = `
      <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif;">
        <thead style="background-color: #f2f2f2;">
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Course</th>
            <th>Module</th>
            <th>Assignment</th>
            <th>Quiz</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
    `;

      let marksTableRows = "";
      let studentRowCounter = 1;

      sortedStudents.forEach(student => {
        const fullName = student.fullName || "N/A";
        const courses = student.marks || {};
        const courseEntries = Object.entries(courses).sort(([courseA], [courseB]) => courseA.localeCompare(courseB));

        let studentHasEntries = false;
        let courseRowCounter = 0;

        courseEntries.forEach(([course, modules]) => {
          const moduleEntries = Object.entries(modules).sort(([modA], [modB]) => modA.localeCompare(modB));

          if (moduleEntries.length > 0) {
            studentHasEntries = true;
            courseRowCounter += moduleEntries.length;
          }
        });

        if (!studentHasEntries) {
          marksTableRows += `
          <tr>
            <td>${studentRowCounter++}</td>
            <td>${fullName}</td>
            <td colspan="5" style="text-align: center;">No marks recorded</td>
          </tr>
        `;
          return;
        }

        let isFirstCourse = true;
        let modulesProcessed = 0;

        courseEntries.forEach(([course, modules], courseIndex) => {
          const moduleEntries = Object.entries(modules).sort(([modA], [modB]) => modA.localeCompare(modB));

          if (moduleEntries.length === 0) return;

          moduleEntries.forEach(([module, scores], moduleIndex) => {
            if (isFirstCourse && moduleIndex === 0) {
              marksTableRows += `
              <tr>
                <td rowspan="${courseRowCounter}">${studentRowCounter++}</td>
                <td rowspan="${courseRowCounter}">${fullName}</td>
                <td rowspan="${moduleEntries.length}">${course}</td>
                <td>${module}</td>
                <td>${scores.assignment ?? "N/A"}</td>
                <td>${scores.quiz ?? "N/A"}</td>
                <td>${scores.comment ?? ""}</td>
              </tr>
            `;
            } else if (moduleIndex === 0) {
              marksTableRows += `
              <tr>
                <td rowspan="${moduleEntries.length}">${course}</td>
                <td>${module}</td>
                <td>${scores.assignment ?? "N/A"}</td>
                <td>${scores.quiz ?? "N/A"}</td>
                <td>${scores.comment ?? ""}</td>
              </tr>
            `;
            } else {
              marksTableRows += `
              <tr>
                <td>${module}</td>
                <td>${scores.assignment ?? "N/A"}</td>
                <td>${scores.quiz ?? "N/A"}</td>
                <td>${scores.comment ?? ""}</td>
              </tr>
            `;
            }
          });

          isFirstCourse = false;
          modulesProcessed += moduleEntries.length;
        });
      });

      const marksTableFooter = `</tbody></table>`;

      // Module Overview Table
      const programInfoSnapshot = await getDocs(
        collection(db, `CorporateClients/${companyCode}/programInfo`)
      );
      const courseList = programInfoSnapshot.docs.map((doc) => doc.id);

      const allModules = [];
      for (const course of courseList) {
        const modulesSnapshot = await getDocs(
          collection(db, `CorporateClients/${companyCode}/programInfo/${course}/modules`)
        );
        const modulesData = modulesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          course,
        }));
        allModules.push(...modulesData);
      }

      // Group modules by course
      const modulesByCourse = {};
      allModules.forEach(module => {
        if (!modulesByCourse[module.course]) {
          modulesByCourse[module.course] = [];
        }
        modulesByCourse[module.course].push(module);
      });

      // Sort courses alphabetically
      const sortedCourses = Object.keys(modulesByCourse).sort((a, b) => a.localeCompare(b));

      // Sort modules within each course alphabetically
      sortedCourses.forEach(course => {
        modulesByCourse[course].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      });

      const moduleTableHeader = `
      <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif;">
        <thead>
          <tr style="background-color: #f2f2f2;">
            <th>#</th>
            <th>Course</th>
            <th>Module Name</th>
            <th>Status</th>
            <th>Start Date</th>
            <th>End Date</th>
          </tr>
        </thead>
        <tbody>
    `;

      let moduleTableRows = "";
      let moduleRowCounter = 1;

      // Generate module table rows with proper rowspan
      sortedCourses.forEach(course => {
        const modules = modulesByCourse[course];
        const rowspan = modules.length;

        moduleTableRows += `
        <tr>
          <td rowspan="${rowspan}">${moduleRowCounter++}</td>
          <td rowspan="${rowspan}">${course || "N/A"}</td>
          <td>${modules[0].name || "N/A"}</td>
          <td>${modules[0].status || "N/A"}</td>
          <td>${modules[0].startDate || "N/A"}</td>
          <td>${modules[0].endDate || "N/A"}</td>
        </tr>
      `;

        // Add remaining modules for this course
        for (let i = 1; i < modules.length; i++) {
          moduleTableRows += `
          <tr>
            <td>${modules[i].name || "N/A"}</td>
            <td>${modules[i].status || "N/A"}</td>
            <td>${modules[i].startDate || "N/A"}</td>
            <td>${modules[i].endDate || "N/A"}</td>
          </tr>
        `;
        }
      });

      const moduleTableFooter = `</tbody></table>`;

      const messageHtml = `
      <div style="font-family: Arial, sans-serif; font-size: 14px;">
        <p>Dear Admin,</p>
        
        <p>
          Please find below the <strong>attendance and module marks summary</strong> for <strong>${companyName}</strong>.
        </p>
    
        <h3 style="margin-top: 20px;">Attendance Summary:</h3>
        ${tableHeader + tableRows + tableFooter}
    
        <h3 style="margin-top: 20px;">Module Marks Summary:</h3>
        ${marksTableHeader + marksTableRows + marksTableFooter}
    
        <h3 style="margin-top: 20px;">Module Overview:</h3>
        ${moduleTableHeader + moduleTableRows + moduleTableFooter}
    
        <p style="margin-top: 20px;">
          Please feel free to reach out in case of any queries or clarifications.
        </p>
    
        <p>Best regards,<br/>
        <strong>Naveen Pn</strong><br/>
        Corporate Trainer</p>
      </div>
    `;

      for (const email of adminEmails) {
        const templateParams = {
          to_email: email,
          message: messageHtml,
          title: "Comprehensive Student Summary"
        };

        await emailjs.send(
          "service_m0uhadr",
          "template_zniheem",
          templateParams,
          "uKaFTJVfykxCI6cPs"
        );
        console.log(`Email sent to ${email}`);
      }

      alert("Attendance, marks, and module summary emails sent to all admin emails.");
    } catch (error) {
      console.error("Error fetching/sending data:", error);
      alert("Failed to fetch or send data.");
    }
  };

  const handleModuleSummaryShare = async (e, companyCode, adminEmails, companyName) => {
    e.preventDefault();

    if (!companyCode) {
      alert("Company code is missing.");
      return;
    }

    try {
      const programInfoSnapshot = await getDocs(
        collection(db, `CorporateClients/${companyCode}/programInfo`)
      );
      const courseList = programInfoSnapshot.docs.map((doc) => doc.id);

      const allModules = [];

      for (const course of courseList) {
        const modulesSnapshot = await getDocs(
          collection(db, `CorporateClients/${companyCode}/programInfo/${course}/modules`)
        );
        const modulesData = modulesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          course,
        }));
        allModules.push(...modulesData);
      }

      if (allModules.length === 0) {
        alert("No modules found.");
        return;
      }

      // Group modules by course
      const modulesByCourse = {};
      allModules.forEach(module => {
        if (!modulesByCourse[module.course]) {
          modulesByCourse[module.course] = [];
        }
        modulesByCourse[module.course].push(module);
      });

      // Sort courses alphabetically
      const sortedCourses = Object.keys(modulesByCourse).sort((a, b) => a.localeCompare(b));

      // Sort modules within each course alphabetically
      sortedCourses.forEach(course => {
        modulesByCourse[course].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      });

      const tableHeader = `
        <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif;">
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th>#</th>
              <th>Course</th>
              <th>Module</th>
              <th>Status</th>
              <th>Start Date</th>
              <th>End Date</th>
            </tr>
          </thead>
          <tbody>
      `;

      let tableRows = "";
      let rowCounter = 1;

      // Generate table rows with proper rowspan for courses and serial numbers
      sortedCourses.forEach(course => {
        const modules = modulesByCourse[course];
        const rowspan = modules.length;

        tableRows += `
          <tr>
            <td rowspan="${rowspan}">${rowCounter}</td>
            <td rowspan="${rowspan}">${course || "N/A"}</td>
            <td>${modules[0].name || "N/A"}</td>
            <td>${modules[0].status || "N/A"}</td>
            <td>${modules[0].startDate || "N/A"}</td>
            <td>${modules[0].endDate || "N/A"}</td>
          </tr>
        `;

        // Add remaining modules for this course
        for (let i = 1; i < modules.length; i++) {
          tableRows += `
            <tr>
              <td>${modules[i].name || "N/A"}</td>
              <td>${modules[i].status || "N/A"}</td>
              <td>${modules[i].startDate || "N/A"}</td>
              <td>${modules[i].endDate || "N/A"}</td>
            </tr>
          `;
        }

        rowCounter++;
      });

      const tableFooter = `
          </tbody>
        </table>
      `;

      const messageHtml = `
      <div style="font-family: Arial, sans-serif; font-size: 14px;">
        <p>Dear Admin,</p>
        <p>
          Please find below the <strong>module summary</strong> for <strong>${companyName}</strong>.
        </p>
        ${tableHeader + tableRows + tableFooter}
        <p>Best regards,<br/>
        <strong>Naveen Pn</strong><br/>
        Corporate Trainer</p>
      </div>
    `;


      for (const email of adminEmails) {
        const templateParams = {
          to_email: email,
          message: messageHtml,
          title: `Module Completion Status ${companyName}`
        };

        await emailjs.send(
          "service_m0uhadr",
          "template_zniheem",
          templateParams,
          "uKaFTJVfykxCI6cPs"
        );
        console.log(`Email sent to ${email}`);
      }
      alert("Module summary emails sent to all admin emails.");
    } catch (error) {
      console.error("Error fetching/sending module data:", error);
      alert("Failed to fetch modules or send emails.");
    }
  };

  const downloadExcel = (htmlTable, fileName) => {
    const template = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Sheet1</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body>${htmlTable}</body></html>`;
    const blob = new Blob([template], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateAttendanceExcel = () => {
    if (students.length === 0) return alert("No data available");
    const sortedStudents = [...students].sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
    const allDates = new Set();
    sortedStudents.forEach(student => {
      if (student.attendance) {
        Object.keys(student.attendance).forEach(date => allDates.add(date));
      }
    });
    const attendanceDates = Array.from(allDates).sort();

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      if (!isNaN(d)) return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      return dateStr;
    };

    let htmlContent = `
      <table border="1" style="border-collapse: collapse; text-align: center; font-family: Arial, sans-serif;">
        <tr><td colspan="${attendanceDates.length + 3}" style="text-align: center; font-size: 20px; font-weight: bold; color: #004080; padding: 15px;">Attendance Summary</td></tr>
        <tr><td colspan="${attendanceDates.length + 3}" style="padding: 10px; background-color: #f8f9fa;"><strong>Client Name:</strong> ${companyName} &nbsp;|&nbsp; <strong>Program Name:</strong> ${programName} &nbsp;|&nbsp; <strong>Trainer's Name:</strong> ${trainerName}</td></tr>
        <tr>
          <th style="background-color: #1e3a8a; color: white; padding: 10px;">Student Name</th>
          ${attendanceDates.map(date => `<th style="background-color: #1e3a8a; color: white; padding: 10px;">${formatDate(date)}</th>`).join('')}
          <th style="background-color: #115e59; color: white; padding: 10px;">Total Days</th>
          <th style="background-color: #115e59; color: white; padding: 10px;">Attendance %</th>
        </tr>
    `;

    sortedStudents.forEach((student, index) => {
      const attendance = student.attendance || {};
      let presentDays = 0;
      Object.values(attendance).forEach(status => {
        if (status === 'Present' || status === 'Late') presentDays++;
      });
      const totalDays = Object.keys(attendance).length;
      const percentage = totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(2) : 0;
      const rowBg = index % 2 === 0 ? '#ffffff' : '#f9fafb';

      htmlContent += `<tr style="background-color: ${rowBg};">`;
      htmlContent += `<td style="text-align: left; padding: 8px; font-weight: bold; color: black;">${student.fullName || "N/A"}</td>`;
      attendanceDates.forEach(date => {
        const status = attendance[date] || 'Absent';
        let bgColor = '#d9534f';
        let textColor = 'white';
        if (status.toLowerCase() === 'present') { bgColor = '#5cb85c'; }
        else if (status.toLowerCase() === 'late') { bgColor = '#f0ad4e'; }
        htmlContent += `<td style="background-color: ${bgColor}; color: ${textColor}; padding: 8px; font-weight: bold;">${status ? status.charAt(0) : '-'}</td>`;
      });
      htmlContent += `<td style="padding: 8px; background-color: #f0fdfa; font-weight: bold; color: black;">${totalDays}</td>`;
      htmlContent += `<td style="padding: 8px; background-color: #f0fdfa; font-weight: bold; color: black;">${percentage}%</td>`;
      htmlContent += `</tr>`;
    });

    htmlContent += `</table>`;
    downloadExcel(htmlContent, `${companyName}_Attendance_Summary.xls`);
  };

  const generateMarksExcel = () => {
    if (students.length === 0) return alert("No data available");
    const sortedStudents = [...students].sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));

    const coursesSet = new Set();
    const allModulesMap = new Map();
    sortedStudents.forEach(student => {
      if (student.marks) {
        Object.keys(student.marks).forEach(course => {
          coursesSet.add(course);
          const courseMarks = student.marks[course] || {};
          Object.keys(courseMarks).forEach(module => {
            const key = `${course}-${module}`;
            if (!allModulesMap.has(key)) {
              allModulesMap.set(key, { course, module });
            }
          });
        });
      }
    });

    const isCapstoneCourse = (courseName) => /capstone/i.test(String(courseName || ''));
    const assessmentCourses = Array.from(coursesSet).filter(c => !isCapstoneCourse(c)).sort();
    const allModulesList = Array.from(allModulesMap.values());

    if (assessmentCourses.length === 0) return alert("No assessment data available");

    let htmlContent = ``;

    assessmentCourses.forEach(courseName => {
      const isCompetencyAssessment = /competency/i.test(courseName);
      const colSpanCount = isCompetencyAssessment ? 2 : 3;
      const courseModules = allModulesList.filter(m => m.course === courseName).map(m => m.module).sort();

      if (courseModules.length === 0) return;

      htmlContent += `
        <table border="1" style="border-collapse: collapse; text-align: center; font-family: Arial, sans-serif; margin-bottom: 30px;">
          <tr><td colspan="${colSpanCount * courseModules.length + 1}" style="text-align: center; font-size: 20px; font-weight: bold; color: #004080; padding: 15px; background-color: #e6f2ff;">${courseName} - Marks Summary</td></tr>
          <tr><td colspan="${colSpanCount * courseModules.length + 1}" style="padding: 10px; background-color: #f8f9fa;"><strong>Client Name:</strong> ${companyName} &nbsp;|&nbsp; <strong>Program Name:</strong> ${programName} &nbsp;|&nbsp; <strong>Trainer's Name:</strong> ${trainerName}</td></tr>
          <tr>
            <th rowspan="2" style="background-color: #004080; color: white; padding: 10px; vertical-align: middle;">Student Name</th>
            ${courseModules.map(module => `<th colspan="${colSpanCount}" style="background-color: #0A5F73; color: white; padding: 8px;">${module}</th>`).join('')}
          </tr>
          <tr>
            ${courseModules.map(module => `
              ${!isCompetencyAssessment ? `<th style="background-color: #0E8099; color: white; padding: 6px;">Assignment<br><span style="font-size: 10px;">(Out of 25)</span></th>` : ''}
              <th style="background-color: #0E8099; color: white; padding: 6px;">Quiz<br><span style="font-size: 10px;">(Out of 25)</span></th>
              <th style="background-color: #3ca15e; color: white; padding: 6px;">Percentage (%)</th>
            `).join('')}
          </tr>
      `;

      sortedStudents.forEach((student, index) => {
        const marks = student.marks || {};
        const courseMarks = marks[courseName] || {};
        const rowBg = index % 2 === 0 ? '#ffffff' : '#f9fafb';

        htmlContent += `<tr style="background-color: ${rowBg};">`;
        htmlContent += `<td style="text-align: left; padding: 8px; font-weight: bold; color: black;">${student.fullName || "N/A"}</td>`;
        
        courseModules.forEach(module => {
          const moduleMarks = courseMarks[module] || {};
          const quiz = parseFloat(moduleMarks.quiz);
          const assignment = parseFloat(moduleMarks.assignment);
          const hasQuiz = !Number.isNaN(quiz);
          const hasAssignment = !Number.isNaN(assignment);
          let totalScore = 0;
          let totalMax = 0;
          if (hasQuiz) { totalScore += quiz; totalMax += 25; }
          if (hasAssignment && !isCompetencyAssessment) { totalScore += assignment; totalMax += 25; }
          const percentage = totalMax > 0 ? ((totalScore / totalMax) * 100).toFixed(1) : '-';

          if (!isCompetencyAssessment) {
            const assignmentVal = moduleMarks.assignment !== undefined && moduleMarks.assignment !== null && moduleMarks.assignment !== '' ? moduleMarks.assignment : 'NA';
            htmlContent += `<td style="padding: 8px; background-color: white; color: black;">${assignmentVal}</td>`;
          }
          htmlContent += `<td style="padding: 8px; background-color: white; color: black;">${moduleMarks.quiz || '-'}</td>`;
          htmlContent += `<td style="padding: 8px; background-color: #e8f5e9; font-weight: bold; color: black;">${percentage !== '-' ? percentage + ' %' : '-'}</td>`;
        });
        htmlContent += `</tr>`;
      });
      htmlContent += `</table><br/>`;
    });

    downloadExcel(htmlContent, `${companyName}_Marks_Summary.xls`);
  };

  const generateModuleSummaryExcel = async () => {
    try {
      const modulesRef = collection(db, "modules");
      const modulesQuery = query(modulesRef, where("companyCode", "==", companyCode));
      const modulesSnapshot = await getDocs(modulesQuery);
      
      const modulesList = [];
      modulesSnapshot.forEach(doc => {
        modulesList.push({ id: doc.id, ...doc.data() });
      });

      if (modulesList.length === 0) return alert("No module data available");

      const grouped = {};
      modulesList.forEach(mod => {
        const cName = mod.courseName || "Other";
        if (!grouped[cName]) grouped[cName] = [];
        grouped[cName].push(mod);
      });

      let htmlContent = `
        <table border="1" style="border-collapse: collapse; text-align: center; font-family: Arial, sans-serif;">
          <tr><td colspan="4" style="text-align: center; font-size: 20px; font-weight: bold; color: #004080; padding: 15px;">Module Progress Report</td></tr>
          <tr><td colspan="4" style="padding: 10px; background-color: #f8f9fa;"><strong>Client Name:</strong> ${companyName} &nbsp;|&nbsp; <strong>Program Name:</strong> ${programName} &nbsp;|&nbsp; <strong>Trainer's Name:</strong> ${trainerName}</td></tr>
      `;

      Object.keys(grouped).sort().forEach(course => {
        htmlContent += `
          <tr><td colspan="4" style="background-color: #1e3a8a; color: white; padding: 10px; font-size: 14px; font-weight: bold;">${course}</td></tr>
          <tr style="background-color: #115e59; color: white;">
            <th style="padding: 10px; text-align: left; background-color: #115e59;">Module Name</th>
            <th style="padding: 10px; background-color: #115e59;">Start Date</th>
            <th style="padding: 10px; background-color: #115e59;">End Date</th>
            <th style="padding: 10px; background-color: #115e59;">Status</th>
          </tr>
        `;

        grouped[course].forEach((module, index) => {
          const statusStr = String(module.status || '').toLowerCase();
          let badgeBg = '#ffffff';
          let badgeColor = '#000000';
          if (statusStr.includes('active')) { badgeBg = '#d4edda'; badgeColor = '#155724'; }
          else if (statusStr.includes('not')) { badgeBg = '#f8d7da'; badgeColor = '#721c24'; }
          else if (statusStr.includes('completed')) { badgeBg = '#cfe2ff'; badgeColor = '#084298'; }
          else if (statusStr.includes('in')) { badgeBg = '#fff3cd'; badgeColor = '#856404'; }

          const rowBg = index % 2 === 0 ? '#ffffff' : '#f9fafb';
          htmlContent += `
            <tr style="background-color: ${rowBg};">
              <td style="text-align: left; padding: 10px; font-weight: bold; color: black;">${module.name || module.moduleName || "N/A"}</td>
              <td style="padding: 10px; color: black;">${module.startDate || "-"}</td>
              <td style="padding: 10px; color: black;">${module.endDate || "-"}</td>
              <td style="padding: 10px; background-color: ${badgeBg}; color: ${badgeColor}; font-weight: bold;">${module.status || "N/A"}</td>
            </tr>
          `;
        });
      });

      htmlContent += `</table>`;
      downloadExcel(htmlContent, `${companyName}_Modules_Progress.xls`);

    } catch (error) {
      console.error("Error generating module summary Excel:", error);
      alert("Failed to generate module summary Excel");
    }
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <>
      <Suspense fallback={<div>Loading Navbar...</div>}>
        <NavbarComponent companyName={companyName} />
      </Suspense>
      <div className="share-page-container">
        <div className="pdf-actions">
          <button type="button" onClick={generateAttendanceExcel} className="action-btn attendance-btn">
            <span className="btn-title">Attendance Excel</span>
            <FaFileExcel className="action-icon" />
          </button>

          <button type="button" onClick={generateMarksExcel} className="action-btn marks-btn">
            <span className="btn-title">Marks Excel</span>
            <FaFileExcel className="action-icon" />
          </button>

          <button type="button" onClick={generateModuleSummaryExcel} className="action-btn module-btn">
            <span className="btn-title">Module Excel</span>
            <FaFileExcel className="action-icon" />
          </button>
        </div>
      </div>

      {showCourseModal && (
        <CourseSelectionModal
          courses={availableCourses}
          onSelectCourse={handleCourseSelection}
          onClose={() => setShowCourseModal(false)}
        />
      )}
    </>
  );
};

export default SharePage;